"use client";

import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AddShoppingCart,
  CheckCircleOutline,
  RemoveShoppingCart,
} from "@mui/icons-material";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { GetProductDto } from "src/services/generated-api";
import CardPill from "./card-pill";
import ProductModal from "./product-modal";
import { useLocale } from "next-intl";
import { NUMBER_UNITS_SHOW_LIMITED } from "src/constants/number-of-units-limited";
import getProductPrice, {
  getProductBasePrice,
  productHasActionPrice,
} from "src/utils/get-product-price.util";
import { getCurrencyBasedOnDomain } from "src/utils/domain.util";
import { formatCurrency } from "@configs/currencies";
import { useCartDrawer } from "@contexts/cart-drawer-context";
import { resolveLocalizedName } from "src/utils/resolve-localized-name";
import { productCardFacts } from "src/utils/product-card-facts";
import { resizedPhotoUrl } from "src/components/order-photos-table/photo-url";
import type { ProductWithAttributes } from "src/utils/product-matcher.util";

/**
 * Four columns exist on the API's product but not yet in the generated
 * client — regenerating it needs the backend live on :3333 plus Java and
 * rewrites the whole client, so it is deferred. Same local-extension pattern
 * as RawDefinition in app/[locale]/produkti/page.tsx. `createdAt` is not
 * declared on GetProductDto either, but the backend's CRUD serializer
 * exposes every own key of the entity regardless of the DTO's declared
 * shape, so it is present on the wire — see product-store/index.tsx's sort.
 *
 * Defined in product-matcher.util.ts (which getRelevantProducts also needs
 * and which this file does not depend on) and re-exported here so existing
 * imports of `ProductWithAttributes` from this module keep working.
 */
export type { ProductWithAttributes };

interface Props {
  product: GetProductDto;
  addProduct?: (productId: string, quantity: number) => Promise<void>;
  /** Set an existing line's quantity; 0 removes it. Undefined disables the minus button. */
  setProductQuantity?: (productId: string, quantity: number) => Promise<void>;
  cartQuantities?: Record<string, number>;
  /**
   * How many photos the customer is ordering. Supplied only where order
   * context exists (the recommendation strip). Without it the card states
   * facts but never claims a fit.
   */
  userPhotoCount?: number;
  /** Open this product's dialog on mount (a bundle slot's link names it). */
  autoOpen?: boolean;
}

const SingleProduct: React.FC<Props> = ({
  product,
  addProduct,
  setProductQuantity,
  cartQuantities,
  userPhotoCount,
  autoOpen,
}) => {
  const translate = useAppTranslate();
  const currentLocale = useLocale();
  const theme = useTheme();
  // Was `const width = window.innerWidth` read once during render: it never
  // responded to a resize or rotation, and referenced `window` in a component
  // Next will try to render on the server.
  const widthMoreThan600 = useMediaQuery(theme.breakpoints.up("sm"));
  const { openCartDrawer } = useCartDrawer();

  const quantity = cartQuantities?.[product.id] ?? 0;
  const [modalOpen, setModalOpen] = useState(() => !!autoOpen);
  // Also when the link is followed on the already-mounted page (adjusted
  // while rendering, react.dev "adjusting state when a prop changes").
  const [prevAutoOpen, setPrevAutoOpen] = useState(autoOpen);
  if (autoOpen !== prevAutoOpen) {
    setPrevAutoOpen(autoOpen);
    if (autoOpen) setModalOpen(true);
  }
  const showLastUnitsBadge = product.nOfUnits <= NUMBER_UNITS_SHOW_LIMITED;

  const handleAdd = async () => {
    if (product.nOfUnits <= quantity) return;
    openCartDrawer();
    if (!addProduct) return;
    try {
      await addProduct(product.id, quantity + 1);
    } catch (error) {
      console.error("Failed to sync product addition with server:", error);
    }
  };

  const handleRemove = async () => {
    if (!setProductQuantity || quantity <= 0) return;
    try {
      await setProductQuantity(product.id, quantity - 1);
    } catch (error) {
      console.error("Failed to sync product removal with server:", error);
    }
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const currency = getCurrencyBasedOnDomain();
  const hasActionPrice = productHasActionPrice(product, currency);
  const basePrice = getProductBasePrice(product, currency);
  const basePriceDivided = basePrice ? basePrice / 100 : 0;
  const price = getProductPrice(product, currency);
  const priceDivided = price ? price / 100 : 0;

  let language = currentLocale === "sl" ? "si" : currentLocale || "en";
  language = language.split("-")[0];
  // currentLocale unsplit first: the admin form seeds product.name under the
  // verbatim AVAILABLE_LANGUAGES key, which includes "de-AT". "si" is the
  // admin form's Slovenian key, not "sl".
  const productName = resolveLocalizedName(
    product.name as Record<string, string> | undefined,
    [currentLocale, language, "si", "en"]
  );
  // alt="" would mark a product photo decorative to a screen reader.
  const productAltName = productName || translate("product.productShowcase");

  // Facts, not assumptions. A frame yields a size and no capacity; something
  // unparseable yields neither and simply renders fewer lines.
  // Memoized: the store renders ~90 of these cards and re-renders all of them
  // on every cart-quantity change. productCardFacts calls parseProductName,
  // which console.logs on every path — unmemoized, that's ~90 log lines per
  // render on a production checkout screen.
  const attrs = product as ProductWithAttributes;
  const facts = useMemo(
    () =>
      productCardFacts(productName, userPhotoCount, {
        capacity: attrs.capacity,
        photoSize: attrs.photoSize,
      }),
    [productName, userPhotoCount, attrs.capacity, attrs.photoSize]
  );

  return (
    <>
      <Paper
        elevation={3}
        sx={(t) => ({
          width: "100%",
          maxWidth: widthMoreThan600 ? 260 : 170,
          // The container gives us alignItems: "stretch" now, but that only
          // sizes this Paper to match its siblings if the Paper itself is
          // willing to take 100% of the stretched height — otherwise it
          // still just hugs its own content.
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          borderRadius: "12px",
          border:
            quantity > 0 ? `2px solid ${t.palette.primary.main}` : "2px solid transparent",
          transition: "box-shadow .2s ease, transform .2s ease",
          ":hover": {
            boxShadow: "0 8px 16px 0 rgba(0,0,0,0.2)",
            transform: "translateY(-2px)",
          },
        })}
      >
        {/* The frame. Its shape is the whole point: the catalogue's first
            photos are 26 portrait (0.726) to 5 square (1.0) among the 31
            products the store actually shows, and a 3:4 frame renders the
            portrait majority at 97% fill instead of the 73% a square frame
            gave them. `contain` still guarantees nothing is ever cropped.
            Position is relative because the pills are absolutely placed
            against this box, not against the card. */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "3 / 4",
            overflow: "hidden",
            backgroundColor: "action.hover",
          }}
        >
          {/* A real button, so the modal has a keyboard route. It used to be an
              onClick on a Box and a Typography, reachable by mouse only. */}
          <Box
            component="button"
            type="button"
            onClick={toggleModal}
            aria-label={productAltName}
            sx={{
              appearance: "none",
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
              display: "block",
              width: "100%",
              height: "100%",
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: "-2px",
              },
              // Touch devices report no hover; without this guard the scale
              // sticks after a tap on mobile.
              "@media (hover: hover)": {
                "&:hover img": { transform: "scale(1.03)" },
              },
            }}
          >
            {/* Resized: the originals are ~460 KB at 886x1221 for a tile
                about 256 px wide, and they are the first thing a visitor
                waits for. The catalogue URL holds for the day (backend
                PhotoService.getDailySignedUrl), so Next's cache hits. */}
            <img
              src={
                resizedPhotoUrl(product.photos?.[0]?.url, 640) ||
                "/images/product/no-image.png"
              }
              alt={productAltName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
                transition: "transform .3s ease",
              }}
            />
          </Box>

          {showLastUnitsBadge && (
            <CardPill
              tone="warning"
              placement="top-right"
              label={translate("product.lastUnits")}
            />
          )}

          {facts.fits && (
            <CardPill
              tone="success"
              placement="bottom-left"
              icon={<CheckCircleOutline />}
              label={translate(
                "product.fitsYourPhotos",
                "fits your {{count}} photos",
                { count: userPhotoCount }
              )}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            p: 1,
            flexGrow: 1,
          }}
        >
          {/* Not a click target: the image button above already opens the
              modal and is the first tab stop. A second, redundant onClick
              here used to have no keyboard route at all while still looking
              clickable (cursor: pointer, hover color) — dropped both rather
              than add a second tab stop for the same action. */}
          <Typography
            variant={widthMoreThan600 ? "subtitle1" : "body2"}
            component="h3"
            sx={{
              fontWeight: 600,
              lineHeight: 1.25,
              // Two lines instead of 13 characters. 24% of the catalogue used
              // to render a label it shared with another product.
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: "2.5em",
            }}
          >
            {productName}
          </Typography>

          {facts.attributes && (
            <Typography variant="caption" color="text.secondary">
              {facts.attributes}
            </Typography>
          )}

          {/* One row: prices on the left, cart controls on the right.
              `flexWrap` is load-bearing rather than decorative — once a
              quantity is showing, flex breaks the controls to their own line
              at the 170px card width in every currency. The break uses each
              item's max-content size before shrinking; nothing clips. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mt: "auto",
              pt: 0.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                flex: "1 1 auto",
              }}
            >
              {hasActionPrice && !!basePrice && (
                <Typography
                  variant="caption"
                  component="span"
                  color="text.secondary"
                  sx={{ textDecoration: "line-through", lineHeight: 1.2 }}
                >
                  {formatCurrency(basePriceDivided, currency)}
                </Typography>
              )}
              {/* Not red. 28 of the 31 products the store shows carry an
                  action price, so colouring discounts red painted nearly
                  every card red — no signal, and a permanent-clearance look
                  on albums people buy for weddings. The struck-through
                  original above carries the saving on its own. */}
              <Typography
                variant={widthMoreThan600 ? "subtitle1" : "body2"}
                fontWeight={700}
                color="text.primary"
                sx={{ lineHeight: 1.2 }}
              >
                {formatCurrency(priceDivided, currency)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              {quantity > 0 && (
                <>
                  <Tooltip title={translate("product.removeFromCart")}>
                    <Button
                      variant="outlined"
                      onClick={handleRemove}
                      sx={{ minWidth: 0, px: 1 }}
                    >
                      <RemoveShoppingCart fontSize="small" />
                    </Button>
                  </Tooltip>
                  <Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>
                    {quantity}
                  </Typography>
                </>
              )}
              <Tooltip
                title={
                  product.nOfUnits <= quantity
                    ? translate("product.maxQuantityReached", "Maximum quantity reached")
                    : translate("product.addToCart")
                }
              >
                {/* span keeps the tooltip alive while the button is disabled */}
                <span>
                  <Button
                    variant="contained"
                    onClick={handleAdd}
                    disabled={!addProduct || product.nOfUnits <= quantity}
                    sx={{ minWidth: 0, px: 1.5 }}
                  >
                    <AddShoppingCart fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Paper>

      <ProductModal
        product={product}
        open={modalOpen}
        onClose={toggleModal}
        addProduct={addProduct}
        setProductQuantity={setProductQuantity}
        cartQuantities={cartQuantities}
      />
    </>
  );
};

// Still imported by product-modal.tsx — deleting this breaks it.
// (photo-preview/index.tsx looks like a second consumer but defines its own
// local buttonStyle; it does not import this one.)
export const buttonStyle = {
  background: "none",
  border: "none",
  padding: "0px 0px 0px 0px",
  cursor: "pointer",
  fontSize: "10px",
  color: "primary.main",
};

export default SingleProduct;
